/**
 * The login-resolution warning copy (ADR-033 §3.2, amended 2026-06-08).
 *
 * The message :class:`PreferencesProvider` raises when a fresh sign-in found
 * the user's chosen login-default Profile missing (a Shared Profile an admin
 * deleted, or a User Profile the user removed) — the UI loads the **built-in
 * Shared default** instead and tells the operator so they can pick a new one
 * in Settings ▸ Profiles.
 *
 * # This file used to carry a bespoke banner component
 *
 * Until 2026-07-26 (#726) it also exported a self-contained
 * ``<DefaultProfileWarning>`` ``role="alert"`` element, because — as its own
 * docstring put it — the app's only other transient-feedback surface was the
 * **visually hidden** screen-reader announcer, so a sighted operator would
 * have seen nothing (CLAUDE.md §8.1). It was a toast in all but name, written
 * because no toast host existed.
 *
 * One now does (``notifications/``), so the provider raises this copy through
 * it and the component is gone: one transient-feedback surface, not two
 * (CLAUDE.md §4.4). The two deliberate behaviour changes that came with the
 * migration — ``sticky`` to preserve persist-until-dismissed, and polite
 * rather than assertive announcement — are recorded at the call site.
 *
 * The copy stays here, and stays a function rather than an inline string, so
 * the provider and its tests share one source of truth for the wording
 * (REQ-UI-037 — clear, non-leaking user-facing copy).
 *
 * Cross-references:
 *   - ADR-033 §3.2 (login resolution — missing default ⇒ built-in + warning).
 *   - REQ-UI-093 (default applied at login); REQ-UI-094 (announced).
 *   - preferences/preferences-provider.tsx (the caller).
 */

export function defaultProfileMissingMessage(name: string): string {
  return `Your default profile “${name}” couldn't be loaded — loading the system default. Choose a new default in Settings ▸ Profiles.`;
}
